import React, {
	useRef,
	useState,
	useMemo,
	type ReactElement,
	type RefObject,
	useCallback,
} from 'react';
import useLayoutEffectSAFE from '@jira/platform__app-framework-use-layout-effect-safe/src/useLayoutEffectSafe.tsx';

type RenderPathLocation = undefined | 'fallback' | 'children';
const ReportLocation = ({
	cb,
	location,
}: {
	cb: (location: RenderPathLocation) => void;
	location: RenderPathLocation;
}) => {
	useLayoutEffectSAFE(() => {
		cb(location);
	}, [cb, location]);
	return null;
};
/**
 * Hook to tracks which part of a "switch" is being rendered - either the fallback or the children.
 */
export const useSwitchControl = (
	_name: string,
): [
	/**
	 * The current render path location - either 'fallback' or 'children'. Or undefined if neither has rendered yet.
	 */
	renderedPath: RenderPathLocation,
	/**
	 * A ref object containing the current render path location.
	 */
	renderedPathRef: RefObject<RenderPathLocation>,
	/**
	 * React elements to be rendered in the fallback and children positions of a switch.
	 */
	renderCallbacks: { fallback: ReactElement; children: ReactElement },
] => {
	const locationRef = useRef<RenderPathLocation>(undefined);
	const [location, setLocationState] = useState<RenderPathLocation>(undefined);

	const setLocation = useCallback((loc: RenderPathLocation) => {
		locationRef.current = loc;
		setLocationState(loc);
	}, []);

	return [
		location,
		locationRef,
		useMemo(
			() => ({
				fallback: <ReportLocation key="unfreeze" cb={setLocation} location="fallback" />,
				children: <ReportLocation key="unfreeze" cb={setLocation} location="children" />,
			}),
			[setLocation],
		),
	];
};
